import { View, StyleSheet, TextInput, Pressable } from 'react-native'
import { SectionTitle } from '../../app/components/Admin/SectionTitle'
import { Metrics } from '../../app/components/Admin/Metrics/Metrics'
import { GamesTable } from '../../app/components/Admin/GamesTable'
import { LoungesTable } from '../../app/components/Admin/LoungesTable/LoungesTable'
import { SearchBar } from '../../app/components/Admin/SearchBar'
import { UsersTable } from '../../app/components/Admin/UsersTable'
import { ScrollView } from 'react-native-gesture-handler'
import { useStore } from '../../app/models'
import { observer } from 'mobx-react-lite'
import { useState, useCallback, useRef } from 'react'
import { UserModelType } from '../../app/models/api-store'
import { useRouter } from 'expo-router'
import { Route } from 'expo-router'
import { DS } from '../../app/design-system'
import { useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'

function QuickActions() {
  const router = useRouter()
  const store = useStore()
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [loungeName, setLoungeName] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const createLounge = useMutation(api.lounges.mutations.createLounge)

  const handleCreate = async () => {
    if (!loungeName.trim()) return
    const user = store.session.user
    if (!user?.auth0Id || user.auth0Id === '') return
    const userId = user.auth0Id
    const userName = user.name ?? 'Unknown'
    setCreating(true)
    setCreateError(null)
    try {
      await createLounge({ name: loungeName.trim(), ownerId: userId, userName })
      setLoungeName('')
      setShowCreateForm(false)
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create lounge')
    } finally {
      setCreating(false)
    }
  }

  return (
    <View style={styles.quickActionsContainer}>
      <View style={styles.quickActionsRow}>
        <Pressable style={styles.actionButton} onPress={() => router.push('/(admin)/reports' as Route)}>
          <DS.Text variant="Body Small/Medium" style={{ color: DS.palette['Brand/Maroon'] }}>
            View Reports →
          </DS.Text>
        </Pressable>

        <Pressable
          style={[styles.actionButton, styles.actionButtonFilled]}
          onPress={() => {
            setShowCreateForm((v) => !v)
            setCreateError(null)
          }}
        >
          <DS.Text variant="Body Small/Medium" style={{ color: DS.palette['Global/White'] }}>
            {showCreateForm ? 'Cancel' : '+ Create Lounge'}
          </DS.Text>
        </Pressable>
      </View>

      {showCreateForm && (
        <View style={styles.createForm}>
          <TextInput
            style={styles.createInput}
            placeholder="Lounge name"
            value={loungeName}
            onChangeText={setLoungeName}
            autoFocus
          />
          <Pressable
            style={[
              styles.actionButton,
              styles.actionButtonFilled,
              (!loungeName.trim() || creating) && styles.actionButtonDisabled
            ]}
            onPress={handleCreate}
            disabled={!loungeName.trim() || creating}
          >
            <DS.Text variant="Body Small/Medium" style={{ color: DS.palette['Global/White'] }}>
              {creating ? 'Creating...' : 'Create'}
            </DS.Text>
          </Pressable>
          {createError && (
            <DS.Text variant="Mini/Regular" style={{ color: DS.palette['Utility/Error'] }}>
              {createError}
            </DS.Text>
          )}
        </View>
      )}
    </View>
  )
}

const Dashboard = observer(() => {
  const store = useStore()
  const [searchResults, setSearchResults] = useState<UserModelType[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const latestSearchId = useRef(0)

  const handleSearch = useCallback(
    async (searchTerm: string) => {
      const searchId = latestSearchId.current + 1
      latestSearchId.current = searchId

      if (!searchTerm || searchTerm.trim().length < 2) {
        setSearchResults([])
        setHasSearched(false)
        setIsSearching(false)
        return
      }

      setIsSearching(true)
      setHasSearched(true)

      try {
        const result = await store.api.querySearchUsers(
          { searchTerm, limit: 50 },
          (qb) => qb._id.name.email.phoneNumber.userRoles.lastLogin.createdAt,
          { fetchPolicy: 'no-cache' }
        )

        if (searchId !== latestSearchId.current) return

        if (result?.searchUsers) {
          setSearchResults(result.searchUsers)
        } else {
          setSearchResults([])
        }
      } catch (error) {
        if (searchId !== latestSearchId.current) return

        console.error('Failed to search users:', error)
        setSearchResults([])
      } finally {
        if (searchId === latestSearchId.current) {
          setIsSearching(false)
        }
      }
    },
    [store.api]
  )

  return (
    <ScrollView>
      <View style={styles.container}>
        <SectionTitle title="Dashboard Overview" subtitle="Real-time insights into your poker application" />
        <QuickActions />
        <Metrics />
        <SearchBar onSearch={handleSearch} />
        {hasSearched && <UsersTable users={searchResults} isLoading={isSearching} />}
        <GamesTable />
        <LoungesTable />
      </View>
    </ScrollView>
  )
})

export default Dashboard

const styles = StyleSheet.create({
  container: {
    gap: 32
  },
  quickActionsContainer: {
    gap: 12
  },
  quickActionsRow: {
    flexDirection: 'row',
    gap: 12
  },
  actionButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Maroon'],
    alignSelf: 'flex-start'
  },
  actionButtonFilled: {
    backgroundColor: DS.palette['Brand/Maroon'],
    borderColor: DS.palette['Brand/Maroon']
  },
  actionButtonDisabled: {
    opacity: 0.5
  },
  createForm: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center'
  },
  createInput: {
    flex: 1,
    height: 40,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    paddingHorizontal: 12,
    backgroundColor: DS.palette['Brand/Cream'],
    fontSize: 14,
    maxWidth: 320
  }
})
